<template>
  <div class="page-container flex-container">
    <ModalBox :visible="showModal" :infoTitle="infoTitle" :infoMessage="infoMessage" @close="showModal = false" />
    <!-- <CustomAnimatedLogo></CustomAnimatedLogo> -->
    
    <!-- <CustomAnimatedLogo v-if="isInProgress"></CustomAnimatedLogo> -->
    <div class="container">
      <br>
      <h5 class="flex-container">PV Generation Estimator</h5>
    
    <form @submit.prevent="submitForm">

          <div class="form-grid">
          
          <!-- Row 1 -->
            <div class="grid-address">
              <label for="locationAddress">Location Address</label>
              <div class="autocomplete-wrapper">
                <input 
                  type="text" 
                  v-model="form.address"
                  @input="onAddressInput"
                  autocomplete="off" 
                  placeholder="Enter your address"
                  :class="{ 'input-error': errors.address }"
                />
                <p v-if="errors.address" class="error-text">
                  {{ errors.address }}
                </p>
          
                <ul v-if="addressSuggestions.length" class="autocomplete-list">
                  <li
                    v-for="(item, index) in addressSuggestions"
                    :key="index"
                    @click="selectAddress(item)"
                    >
                      {{ item.description }}
                  </li>
                </ul>
              </div>
            </div>

            <div> 
              <label for="year">Year</label>
              <div ref="yearDropdownRef" class="select-wrapper">
                <div
                  class="select-display"
                  tabindex="0"
                  @click="toggleYearDropdown"
                >
                {{ form.year || 'Select year' }}
                </div>
                <p v-if="errors.year" class="error-text">
                  {{ errors.year }}
                </p>

                <ul v-if="showYearDropdown" class="select-dropdown">
                  <li
                    v-for="year in years"
                    :key="year"
                    @click="selectYear(year)"
                  >
                  {{ year }}
                </li>
                </ul>
              </div>
            </div>

            <!-- Row 2 -->
            <div>
              <label for="solarCapacity">Solar Capacity (kW)</label>
              <input type="number" min="0" v-model="form.power_max_solar" :class="{ 'input-error': errors.power_max_solar }"/>
              <p v-if="errors.power_max_solar" class="error-text">
                {{ errors.power_max_solar }}
              </p>
            </div>
            

            <div> 
              <label for="maxInverterCapacity">Max Inverter Capacity (kW)</label>
              <input type="number" min="0" v-model="form.power_max_inverter" :class="{ 'input-error': errors.power_max_inverter }"/>
              <p v-if="errors.power_max_inverter" class="error-text">
                {{ errors.power_max_inverter }}
              </p>
            </div>
            

            <!-- Row 3 -->
            <div>
              <label for="latitude">Latitude</label>
              <input type="number" v-model="form.latitude" readonly />
            </div>

            <div>
              <label for="Longitude">Longitude</label>
              <input type="number" v-model="form.longitude" readonly />
            </div>
      
            <!-- Submit -->
            <div class="submit-row">
              <button class="submit-button" type="submit" :disabled="isInProgress">
                Submit
              </button>
            </div>
          </div>

          <!-- <div v-if="isInProgress" class="spinner-wrapper">
            <div class="inline-spinner"></div>
          </div> -->
          <div class="spinner-wrapper">
            <div v-if="isInProgress" class="inline-spinner"></div>
          </div>
        </form>

        <!-- PV daily generation table -->
        <div v-if="!isInProgress && hasSubmitted && tableData.length" class="data-section">

          <div class="table-header-1">
            <div class="header-left">
              <h3>
                PV Generation Data 
                <span v-if="isDataFilterActive">
                  ({{ filteredTableData.length }} records)
                </span>
              </h3>
            </div>

            <div class="date-filters">
              <label>
                Total Annual PV(KWh):
                <input
                  type="text"
                  :value="annualTotalKWh"
                  readonly
                />
              </label>

              <label>
                Select Date:
                <input
                  type="date"
                  v-model="selectedDate"
                  :min="dailyMinDate"
                  :max="dailyMaxDate"
                />
              </label>

              <label>
                Daily Average PV (KWh):
                <input
                  type="text"
                  :value="dailyAverageKWh"
                  readonly
                />
              </label>
            </div>
          </div>

          <div class="view-toggle">
            <span :class="{ activeLabel: viewMode === 'table' }"> Table </span>
            
            <label class="switch">
              <input 
                type="checkbox" 
                :checked="viewMode === 'chart'" 
                @change="setViewMode($event.target.checked ? 'chart' : 'table')"
              />
              <span class="slider"></span>
            </label>

            <span :class="{ activeLabel: viewMode === 'chart' }"> Chart </span>
          </div>

          <div v-if="viewMode === 'table'">
            <div class="table-header-2">
                <label>
                  Start Date
                  <input
                    type="date"
                    v-model="startDate"
                    :min="minDate"
                    :max="maxDate"
                    @change="isDataFilterActive = true"
                  />
                </label>

                <label>
                  End Date
                  <input
                    type="date"
                    v-model="endDate"
                    :min="minDate"
                    :max="maxDate"
                    @change="isDataFilterActive = true"
                  />
                </label>

                <button class="export-button" @click="exportToCSV">
                  Export to CSV
                </button>
              </div>
          
            <div class="table-container">
              <div 
                v-if="isDataFilterActive && !filteredTableData.length" 
                class="no-data-message"
              >
                No PV data available
              </div>

              <table v-else class="data-table">
                <thead>
                  <tr>
                    <th class="data-col">Date</th>
                    <th class="time-col">Time</th>
                    <th class="value-col">PV Generation (kW)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in filteredTableData" :key="idx">
                    <td class="date-col">{{ row.date }}</td>
                    <td class="time-col">{{ row.time }}</td>
                    <td class="value-col">{{ (row.value / 1000).toFixed(4) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-if="viewMode === 'chart'" class="chart-view">

            <!-- Annual Chart -->
            <apexChart
              :key="`annual-${isDarkMode}`"
              type="line"
              height="420"
              :options="chartOptions"
              :series="chartSeries"
            />

            <!-- Intraday Chart -->
             <div v-if="selectedDate && intradaySeriesData.length" class="intraday-chart">
              <!-- <h4>Intraday PV Generation ({{ selectedDate }})</h4> -->
              <apexChart
                :key="`intraday-${isDarkMode}`"
                type="line"
                height="350"
                :options="intradayChartOptions"
                :series="intradayChartSeries"
              />
             </div>
          </div>
        </div>
    </div>
  </div>

    
</template>

<script>
import { ref, nextTick, onMounted, onUnmounted, watch, computed } from 'vue';
import { apiRequest } from '@/services/apiService';
import { Chart } from 'chart.js';
import ModalBox from '@/components/cards/ModalBox.vue';
import CustomAnimatedLogo from '../cards/CustomAnimatedLogo.vue';
import StyledInfoBox from '../cards/StyledInfoBox.vue';
import VueApexCharts from 'vue3-apexcharts';
import StyledInfoWithIcon from '../cards/StyledInfoWithIcon.vue';
import { getDevModeState } from '@/services/cookieService';
import apiClient from '@/services/apiService';
import axios from 'axios';
import { utcYears } from 'd3-time';
import { re } from 'mathjs';
// import { setTimeout } from 'core-js';

// Function to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data URL prefix
    reader.onerror = (error) => reject(error);
  });
};

export default {
  name: 'SimulatorPage',
  components: {
    ModalBox,
    CustomAnimatedLogo,
    StyledInfoBox,
    apexChart: VueApexCharts,
    StyledInfoWithIcon,
  },
  computed: {

  },
  setup() {
    const subDataUrl = ref('/api/v1.1/data');  
    let resultResponseText = ref('');
    let isInProgress = ref(false);

    const tableData = ref([]);
    const currentYear = 2026;
    const years = ref(
      Array.from({length: 5}, (_, i) => currentYear - 1 - i)
    );

    const errors = ref({
      address: '',
      year: '',
      power_max_solar: '',
      power_max_inverter: ''
    });

    const showYearDropdown = ref(false);
    const yearDropdownRef = ref(null);

    //toggle function
    const viewMode = ref('table'); // 'table' or 'chart'

    const setViewMode = (mode) => {
      viewMode.value = mode;
    };

    // Date filters
    const startDate = ref('');
    const endDate = ref('');

    const annualTotalKWh = ref(0);

    //daily average calculation
    const selectedDate = ref('');
    const dailyAverageKWh = ref(0);

    //Restrict date range
    const minDate = computed(() => 
      form.value.year ? `${form.value.year}-01-01` : ''
    );

    const maxDate = computed(() => 
      form.value.year ? `${form.value.year}-12-31` : ''
    );

    //Restrict date range - daily average calculation
    const dailyMinDate = computed(() => 
      form.value.year ? `${form.value.year}-01-01` : ''
    );

    const dailyMaxDate = computed(() => 
      form.value.year ? `${form.value.year}-12-31` : ''
    );

    // Year Dropdown
    const toggleYearDropdown = () => {
      showYearDropdown.value = !showYearDropdown.value;
    }

    const selectYear = (year) => {
      form.value.year = year;
      showYearDropdown.value = false;
    }

    const hasSubmitted = ref(false);

    // validate form
    const validateForm = () => {
      let isValid = true;

      // Reset errors
      errors.value = {
        address: '',
        year: '',
        power_max_solar: '',
        power_max_inverter: ''
      };

      if (!form.value.address.trim()) {
        errors.value.address = 'Address is required';
        isValid = false;
      }

      if (!form.value.year) {
        errors.value.year = 'Year is required';
        isValid = false;
      }

      if (!form.value.power_max_solar || form.value.power_max_solar <= 0) {
        errors.value.power_max_solar = 'Solar capacity is required';
        isValid = false;
      }

      if (!form.value.power_max_inverter || form.value.power_max_inverter <= 0) {
        errors.value.power_max_inverter = 'Max inverter capacity is required';
        isValid = false;
      }

      return isValid;
    };

    // No data message handling
    const isDataFilterActive = ref(false);

    const handleClickOutside = (event) => {
      if (
        yearDropdownRef.value &&
        !yearDropdownRef.value.contains(event.target)
      ) {
        showYearDropdown.value = false;
      }

      const inputWrapper = document.querySelector('.autocomplete-wrapper');
      if(inputWrapper && !inputWrapper.contains(event.target)){
        addressSuggestions.value = [];
      }
    };

    onMounted (() => {
      document.addEventListener('click', handleClickOutside);

      const devModeState = getDevModeState();
      subDataUrl.value = devModeState.isDevMode ? '/dev-api/v1.1/data' : '/api/v1.1/data';
    });

    onUnmounted (() => {
      document.removeEventListener('click', handleClickOutside);
    })

    const form = ref({
      year: '',
      address: '',
      power_max_solar: 0,
      power_max_inverter: 0,
      latitude: 0.0,
      longitude: 0.0
    });

    watch(() => form.value.address, (newVal) => {
      if (!newVal || newVal.trim() === '') {
        form.value.latitude = 0.0;
        form.value.longitude = 0.0;
      }
    });

    const showModal = ref(false);
    const infoTitle = ref('');
    const infoMessage = ref('');

    const addressSuggestions = ref([]);
    let debounceTimer = null;

    // Auto-complete address
    const onAddressInput = () => {
      clearTimeout(debounceTimer);

      if(!form.value.address || form.value.address.length < 3){
        addressSuggestions.value = [];
        return;
      }

      debounceTimer = setTimeout(fetchAddressSuggestions, 400);
    }

    const url_autocomplete = `${subDataUrl.value}/proxy/rx/maps/autocomplete`;
    const url_geocode = `${subDataUrl.value}/proxy/rx/maps/geocode`;
    const url_pv_estimate = `${subDataUrl.value}/proxy/pv/estimate/power/year/get`;

    const fetchAddressSuggestions = async () => {
      try {
        const response = await apiRequest(
          'post',
          url_autocomplete,
          { input: form.value.address }
        );
        // console.log('Autocomplete response:',response);

        addressSuggestions.value = response?.data?.predictions ?? [];
        // console.log('Address Suggestions:', addressSuggestions.value);
        
      } catch (error) {
        console.error('Autocomplete API error:', error);
        addressSuggestions.value = [];
      }
    };

    // Lat, Lng fetching
    const fetchLatLngFromAddress = async (placeId) => {
      try {
        if (!placeId) return;

        const response = await apiRequest(
          'post',
          url_geocode,
          { placeId: placeId }
        );

        const result = response?.data?.results?.[0];
        if (!result) {
          console.warn('No geocode results');
          return;
        }

        const navlocation = result.navigation_points?.[0]?.location;

        if(navlocation){
          form.value.latitude = navlocation.latitude;
          form.value.longitude = navlocation.longitude;
          return;
        }

        const geoLocation = result.geometry?.location;
        if(geoLocation){
          form.value.latitude = geoLocation.lat;
          form.value.longitude = geoLocation.lng;
        }
      } catch (error) {
        console.error('GeoCode API error', error);
      }
    };

    const selectAddress = async (item) => {
      form.value.address = item.description;
      addressSuggestions.value = [];

      if (item.place_id) {
        await fetchLatLngFromAddress(item.place_id);
      } else {
        console.warn('Selected item has no place_id');
      }
    };

    // PV estimator API fetching
    const submitForm = async () => {
      if (!validateForm()) {
        return;
      }

      isInProgress.value = true;
      tableData.value = [];
      isDataFilterActive.value = true;

      try{
        const response = await apiRequest(
          'post',
          url_pv_estimate,
          {
            year: form.value.year,
            address: form.value.address,
            power_max_solar: form.value.power_max_solar,
            power_max_inverter: form.value.power_max_inverter,
            latitude: form.value.latitude,
            longitude: form.value.longitude
          }
        );
        // const data = response?.data;
        // console.log('API Response:', data);
        // const daily = data?.daily ?? {};

        const data = response?.data?.daily ?? {};
        console.log('API Response:', data);
        const unit = response?.data?.data?.unit ?? 'W';

        const rows = [];

        // Generate 5-minute time labels once
        const intervalTimes = Array.from({length: 288}, (_, i) => {
          const hours = Math.floor(i / 12);
          const minutes = (i % 12) * 5;
          return `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}`;
        });
      // console.log('Debug flag 1');

        // Loop each day
        Object.entries(data).forEach(([date, value]) => {
          intervalTimes.forEach((time, idx) => {
            rows.push({
              date,
              time,
              value: value?.[idx] ?? 0,
            });
          });
        });

          //  console.log('Debug flag 2');

        tableData.value = rows;
        hasSubmitted.value = true;

        const totalKWh = rows.reduce((sum, row) => {
          const value = Number(row.value) || 0;
          return sum + value / 12;
        }, 0);

        annualTotalKWh.value = (totalKWh/1000).toFixed(4);
        
      } catch (error) {
        console.error('API Error:', error);
        infoTitle.value = 'Error';
        infoMessage.value = 'Failed to fetch PV estimation. Please try again';
        showModal.value = true;
          //  console.log('Debug flag 3');
      } finally {
        isInProgress.value = false;
          //  console.log('Debug flag 4');
      }
    };

    // filter table data
    const filteredTableData = computed(() => {
      if(!startDate.value && !endDate.value){
        return tableData.value;
      }
        //  console.log('Debug flag 5');

      return tableData.value.filter(row => {
        const rowDate = new Date(row.date);

        const start = startDate.value
          ? new Date(startDate.value)
          : null;

        const end = endDate.value
          ? new Date(endDate.value)
          : null;

        if(start && rowDate < start) return false;
        if(end && rowDate > end) return false;

        return true;
      });
    });

    // Export to CSV
    const exportToCSV = () => {
      if(!filteredTableData.value.length) {
        alert('No data to export');
        return;
      }

      const headers = ['Date', 'Time', 'PV Generation (kW)'];

      const csvRows = filteredTableData.value.map(row => [
        row.date,
        row.time,
        (row.value / 1000).toFixed(4)
      ]);

      const csvContent = [headers, ...csvRows]
        .map(e => e.join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `pv_estimation_${form.value.year}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    // Annual daily generation table
    const annualFiveMinSeries = computed(() => {
      if(!tableData.value.length) return [];

      return tableData.value.map(row => {
        const dateTime = new Date(`${row.date}T${row.time}:00`);

        return {
          x: dateTime,
          y: Number((row.value / 1000).toFixed(4))
        };
      });
    });

    const isDarkMode = ref(document.documentElement.classList.contains('dark-mode'));

    const updateTheme = () => {
      isDarkMode.value = document.documentElement.classList.contains('dark-mode');
    };

    onMounted(() => {
      updateTheme();

      const observer = new MutationObserver(updateTheme);
      observer.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
    });

    const chartOptions = computed(() => ({
      chart: {
        type: 'area',
        background: 'transparent',
        foreColor: isDarkMode.value ? '#ffffff' : '#000000',
        theme: {
          mode: isDarkMode.value ? 'dark' : 'light'
        },
        zoom: { 
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
        },
        toolbar: { autoSelected: 'zoom' },
        animations: { enabled: false },
      },
    stroke: {
      curve: 'straight',
      width: 1
    },
    markers: {
      size: 0
    },
    title: {
      text: `Annual PV Generation (${form.value.year})`,
      align: 'left',
      style: {
        color: isDarkMode.value ? '#ffffff' : '#000000'
      }
    },
    xaxis: {
      type: 'datetime',
      labels: { 
        datetimeUTC: false,
        style: {
          colors: isDarkMode.value ? ['#ffffff'] : ['#000000']
        }
      },
      title: { 
        text: 'Date',
        style: {
          color: isDarkMode.value ? '#ffffff' : '#000000'
        } 
      }
    },
    yaxis: {
      title: { 
        text: 'PV Generation (kW)',
        style: {
          color: isDarkMode.value ? '#ffffff' : '#000000'
        } 
      },
      labels: { 
        style: {
          colors: isDarkMode.value ? ['#ffffff'] : ['#000000']
        }
      }
    },
    tooltip: {
      x: { format: 'dd MMM yyyy HH:mm'},
      theme: isDarkMode.value ? 'dark' : 'light'
    },
    dataLabels: {
      enabled: false
    }  
    }));

    const chartSeries = computed(() => [
      {
        name: 'PV Generation (kW)',
        data: annualFiveMinSeries.value
      }
    ]);


    // Intraday chart
    const intradaySeriesData = computed(() => {
      if(!tableData.value.length || !selectedDate.value) return [];

      const dayRows = tableData.value.filter(row => row.date === selectedDate.value);

      return dayRows.map(row => {
        const isoString = `${row.date}T${row.time}:00`;

        return {
            x: isoString,
            y: Number((row.value / 1000).toFixed(4))
        };
      });
    });

    const intradayChartOptions = computed(() => ({
      chart: {
        type: 'line',
        foreColor: isDarkMode.value ? '#ffffff' : '#000000',
        zoom: { 
          enabled: true,
          type: 'x',
          autoScaleYaxis: true
        },
        // toolbar: { autoSelected: 'zoom' },
        animations: { enabled: false },
      },
      stroke: {
        curve: 'straight',
        width: 2
      },
      markers: {
        size: 0
      },
      title: {
        text: `Intraday PV Generation (${selectedDate.value})`,
        align: 'left',
        style: {
          color: isDarkMode.value ? '#ffffff' : '#000000'
        }
      },
      xaxis: {
        type: 'datetime',
        labels: { 
          datetimeUTC: false,
          format: 'HH:mm',
          style: {
            colors: isDarkMode.value ? ['#ffffff'] : ['#000000']
          } 
        },
        title: { 
          text: 'Time',
          style: {
            color: isDarkMode.value ? '#ffffff' : '#000000'
          } 
        }
      },
      yaxis: {
        title: { 
          text: 'PV Generation (kW)',
          style: {
            color: isDarkMode.value ? '#ffffff' : '#000000'
          } 
        },
        labels: { 
          style: {
            colors: isDarkMode.value ? ['#ffffff'] : ['#000000']
          }
        }
      },
      tooltip: {
        x: { format: 'dd MMM yyyy HH:mm' },
        theme: isDarkMode.value ? 'dark' : 'light'
      },
      dataLabels: {
        enabled: false
      }
    }));

    const intradayChartSeries = computed(() => {
      if (!intradaySeriesData.value.length) return[];
      
      return[
        {
          name: 'Intraday PV generation (kW)',
          data: intradaySeriesData.value
        }
      ]
    });

    watch(
      () => form.value.year,
      (year) => {
        if(!year) {
          startDate.value = '';
          endDate.value = '';
          return;
        }

        startDate.value = `${year}-12-31`;
        endDate.value = `${year}-12-31`;

        isDataFilterActive.value = true;
      }
    );

    watch(
      () => ({ ...form.value}),
      () => {
        tableData.value = [];
        hasSubmitted.value = false;
      },
      { deep: true }
    );

    // Compute daily Average PV
    watch(selectedDate, (date) => {
      if(!date || !tableData.value.length) {
        dailyAverageKWh.value = 0;
        return;
      }

      const dailyRows = tableData.value.filter(
        row => row.date === date
      );

      if(!dailyRows.length) {
        dailyAverageKWh.value = 0;
        return;
      }

      const totalDailyKWh = dailyRows.reduce((sum, row) => {
        const value = Number(row.value) || 0;
        return sum + value / 12;
      }, 0);

      dailyAverageKWh.value = (totalDailyKWh / 1000).toFixed(4);
    });

    watch(
      () => form.value.year,
      () => {
        selectedDate.value = '';
        dailyAverageKWh.value = 0;
      }
    );

    watch(() => form.value.address, () => {
      errors.value.address = '';
    });

    watch(() => form.value.year, () => {
      errors.value.year = '';
    });

    watch(() => form.value.power_max_solar, () => {
      errors.value.power_max_solar = '';
    });

    watch(() => form.value.power_max_inverter, () => {
      errors.value.power_max_inverter = '';
    });

  //  console.log('Debug flag 6');
    return {
      form,
      years,
      showYearDropdown,
      toggleYearDropdown,
      selectYear,
      yearDropdownRef,
      subDataUrl,
      resultResponseText,
      isInProgress,
      showModal,
      infoTitle,
      infoMessage,
      addressSuggestions,
      onAddressInput,
      selectAddress,
      submitForm,
      tableData,
      startDate,
      endDate,
      filteredTableData,
      minDate,
      maxDate,
      hasSubmitted,
      annualTotalKWh,
      selectedDate,
      dailyAverageKWh,
      dailyMinDate,
      dailyMaxDate,
      exportToCSV,
      isDataFilterActive,
      viewMode,
      setViewMode,
      chartOptions,
      chartSeries,
      annualFiveMinSeries,
      intradayChartOptions,
      intradayChartSeries,
      intradaySeriesData,
      errors,
      validateForm,
      isDarkMode
    };
  },
};
</script>

<style scoped>
.container {
  /* padding: 20px;
  max-width: 70vw;
  margin: auto; */
  padding: 20px;
  width: 1000px;
  max-width: 1000px;
  margin: auto;
}

.form-grid {
  display: grid;
  grid-template-columns: 2fr 1;
  gap: 20px;
  width: 100%;
}

.grid-address {
  grid-column: 1 / 2;
}

.submit-row {
  grid-column: 1 / 3;
}

.submit-row button {
  width: 100%;
  height: 45px;
  font-weight: 600;
}

.form-grid input,
.form-grid .select-display {
  height: 40px;
  box-sizing: border-box;
}

.input-section,
.output-section,
.upload-section {
  background: #f4f4f4;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
}

.error-text {
  color: #d32f2f;
  font-size: 0.7rem;
  margin-top: -6px;
  margin-bottom: 6px;
}

.input-error {
  border: 1px solid #d32f2f !important;
}

input,
select,
.submit-button {
  width: 100%;
  padding: 10px;
  margin: 10px 0;
  border-radius: 8px;
  border-width: 0.1px;
}

.export-button {
  background-color: #4CAF50;
  font-weight: 800;
  cursor: pointer;
  font-size: 0.9rem;
  width: 20%;

  padding: 18px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.view-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  font-size: 0.75rem;
}

.view-toggle span {
  font-weight: 500;
  color: #777;
  transition: color 0.2s ease;
}

.view-toggle .activeLabel {
  color: #2e7d32;
  font-weight: 600;
}

/* Swtich Container */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 22px;
}

/* Hide default checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider background */
.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #ccc;
  border-radius: 34px;
  transition: 0.3s;
}

/* Circle Knob */
.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}

/* Checked state */
.switch input:checked + .slider {
  background-color: #4CAF50;
}

.switch input:checked + .slider:before {
  transform: translateX(22px);
}

.chart-view {
  padding: 20px;
  background: var(--card-bg);
}

.intraday-chart {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

input,
select,
button:focus {
  border-width: 0.06px;
  border-color: green;
}

input,
select {
  background-color: var(--card-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

button {
  background-color: #4CAF50;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 8px;
}

button:hover {
  background-color: #45a049;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.twin-fields {
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
}

.twin-fields div {
  width: 48%; /* Adjust width as needed */
}

.charts {
  margin: 20px 0;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  border: 1px solid #ddd;
  padding: 8px;
}

th {
  background-color:rgba(247, 25, 5, 0.9);
  color: white;
}

.important-context {
  font-weight: bold;
}

.autocomplete-wrapper {
  position: relative;
  width: 100%;
}

.autocomplete-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;

  background: var(--card-bg);
  color: var(--text-primary);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  list-style: none;
  padding: 6px 0;
  margin: 0;

  max-height: 220px;
  overflow-y: auto;

  z-index: 1000;
}

.autocomplete-list li {
  padding: 10px 14px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.autocomplete-list li:hover {
  /* background-color: var(--bg-secondary);
  color: var(--text-primary); */
  background-color: #f1f7f4;
  color: #2e7d32;
}

.autocomplete-list li + li {
  border-top: 1px solid #f0f0f0;
}

.select-wrapper {
  position: relative;
  width: 100%;
}

.select-display {
  width: 100%;
  padding: 10px;
  margin: 10px 0;

  border-radius: 8px;
  border: 0.1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-primary);

  font-size: 70%;
  cursor: pointer;

  display: flex;
  align-items: center;
}

.select-display:focus,
.select-display:focus-visible
.select-display:focus-within{
  outline: none;
  border-width: #2684ff;
  border-color: green;
  box-shadow: 0 0 0 1px #2684ff;
}

.select-display:hover {
  border-color: #4CAF50;
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;

  background: var(--card-bg);
  color: var(--text-primary);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  list-style: none;
  margin: 0;
  padding: 6px 0;

  max-height: 108px; /* ≈ 3 items */
  overflow-y: auto;

  z-index: 1000;
}

.select-dropdown li {
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.75rem;
}

.select-dropdown li:hover {
  background-color: #f1f7f4;
  color: #2e7d32;
}

.data-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 12px 10px rgba(0, 0 0, 0.1);
  margin-top: 30px;
}

.table-header-1 {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.table-header-2 {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.table-header-2 label {
  display: flex;
  flex-direction: column;
  font-weight: 500;
  font-size: 0.75rem;
  flex: 1;
}

.table-header-2 input {
  background-color: var(--input-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  font-weight: 400;
  cursor: default;
  font-size: 0.75rem;
  width: 90%;
}

.table-container {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

.data-table th {
  position: sticky;
  top: 0;
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 10px;
  text-align: left;
  font-weight: 600;
}

.data-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.8rem;
}

.data-table tbody tr:nth-child(even) {
  background: var(--bg-secondary);
}

.data-table tbody tr:hover {
  background: var(--bg-primary);
}

.date-col {
  width: 120px;
}

.time-col {
  width: 100px;
}

.value-col {
  width: 180px;
}

.date-filters {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  justify-content: space-between;
}

.date-filters label {
  display: flex;
  flex-direction: column;
  font-weight: 500;
  font-size: 0.75rem;
}

.date-filters input {
  background-color: var(--input-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  font-weight: 400;
  cursor: default;
  font-size: 0.75rem;
}

.date-filters input[read-only] {
  background-color: #f8f9fa;
  font-weight: 400;
  cursor: default;
  font-size: 0.8rem;
}

.no-data-message {
  padding: 24px;
  text-align: center;
  /* color: #666; */
  font-size: 0.85rem;
  font-weight: 500;
  /* background: #fafafa; */
  background-color: var(--input-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.inline-spinner {
  margin-top: 6px;
  width: 16px;
  height: 16px;
  border: 2px solid #ccc;
  border-top-color: #555;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-wrapper {
  display: flex;
  justify-content: center;
  /* margin-top: 8px; */
  height: 24px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}


th, td {
    border: 1px solid #ccc;
    padding: 8px;
    text-align: center;
    font-size: 0.8rem;
  }

  h1, h2, h4, h5, h6, p, label, input, select, button, th, td {
      font-size: 70%; /* Set font size to % of the original size */
  }

  .savings-info{
    display: flex;
    justify-content: space-evenly;
  }

  .custom-table > th, td {
    border: none; /* Makes borders invisible */
    font-size: 1rem; /* Set font size to 2rem */;
  }

  .output-header {
    background-color: white;
    border-radius: 1px;
  }

  table.custom-table thead th {
    background-color: white; /* Use more specific selector */
    color: black; /* Ensure text is visible */
    border-radius: 8px;
    font-size: 1rem;
  }

  table.custom-table {
    border-collapse: separate; /* Change to 'separate' for border radius to work */
    border-spacing: 0; /* Remove default spacing between cells */
    border-radius: 10px; /* Adjust as needed for desired roundness */
    overflow: hidden; /* Prevent content from overflowing */
  }

  #preview-image {
    max-width: 80%;
    height: auto;
    margin: 10px;
  }


  @media (max-width: 750px) {

    th, td {
    border: 1px solid #ccc;
    padding: 3px;
    text-align: center;
    font-size: 0.3rem;
    }

    .savings-info{
      display: flex;
      flex-direction: column;
      justify-content: center; /* Centers items vertically */
      align-items: center; /* Centers items horizontally */
    }

    table.custom-table thead th {
    background-color: white; /* Use more specific selector */
    color: black; /* Ensure text is visible */
    border-radius: 8px;
    font-size: 0.4rem;
    }
}

* {
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}
</style>
